import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  battleshipHitsSelector,
  gameResultSelector,
  gameStatusSelector,
  opponentSelector,
  playGame,
} from '../redux/slices/gameParrotSlice';
import { currentUserSelector } from '../redux/slices/userSlice';
import { getStatusText } from '../components/main/gameParrot/gameUtils';

/**
 * Custom hook for managing a game's state.
 *
 * @returns self - The current signed in user.
 * @returns opponent - The current game opponent.
 * @returns gameStatus - The current game status.
 */
const useGameModal = () => {
  const dispatch = useAppDispatch();

  const selfId = useAppSelector(currentUserSelector)?._id || '';
  const opponent = useAppSelector(opponentSelector);
  const gameStatus = useAppSelector(gameStatusSelector);
  const gameResult = useAppSelector(gameResultSelector);
  const battleshipHits = useAppSelector(battleshipHitsSelector);
  const oppId = opponent?._id || '';
  const challenger = gameStatus.length ? gameStatus[0][0] === selfId : true;
  const gameOver = gameResult[0] !== 'incomplete';
  const canPlay = gameStatus.length
    ? gameStatus[gameStatus.length - 1][0] === oppId && !gameOver
    : false;
  const isChallenge = gameStatus.length ? gameStatus[0][3] === 'challenge' : false;
  const gameType = gameStatus.length ? gameStatus[0][2] : '';

  const statusText = getStatusText(gameOver, gameResult, opponent, canPlay);
  const [gameOverText, setGameOverText] = useState('');
  useEffect(() => {
    if (
      statusText.includes('won') ||
      statusText.includes('lost') ||
      statusText.includes('tie') ||
      statusText.includes('unable')
    ) {
      setGameOverText(statusText);
    } else if (!statusText.includes('has quit')) {
      setGameOverText('');
    }
  }, [statusText]);
  const collectPoint = gameOverText.includes('won') && isChallenge;
  const hideBoard =
    gameOver &&
    !gameOverText.includes('won') &&
    !gameOverText.includes('lost') &&
    !gameOverText.includes('tie');

  /**
   * Function to exit a game after it has ended or while it is being played.
   *
   * @param type - The type of exit, either 'quit' if the game is still going or
   * 'exit' if it has already ended.
   * @param closeFunc - The function to close the game modal currently open.
   */
  const handleExit = (type: 'quit' | 'exit', closeFunc: () => void) => {
    setGameOverText('');
    dispatch(
      playGame({
        going: selfId,
        waiting: oppId,
        data: [],
        type,
      }),
    );
    closeFunc();
  };

  return {
    selfId,
    opponent,
    gameStatus,
    gameResult,
    gameType,
    isChallenge,
    battleshipHits,
    oppId,
    challenger,
    canPlay,
    gameOver,
    statusText,
    gameOverText,
    collectPoint,
    hideBoard,
    handleExit,
  };
};

export default useGameModal;
